import { IonSpinner } from '@ionic/react';
import { ImageList, ImageListItem } from '@mui/material';
import { ReactElement, useCallback, useState } from 'react';
import {
  AttachmentSourceType,
  useDeleteAttachmentMutation,
  useGetChatMessageLazyQuery,
  useGetChatMessageQuery,
} from '../../api/graphql/generated';
import { ImageResolution } from '../../api/hooks/attachment.hooks';
import { AttachmentViewerModal } from '../attachment/attachment-modal';
import { ImagePreview } from '../image/image-preview';

interface ConversationMessageAttachmentListProps {
  conversationId: string;
  allowRemove?: boolean;
  isUploading?: boolean;
}

const ConversationMessageAttachmentList = (
  props: ConversationMessageAttachmentListProps
): ReactElement => {
  const { conversationId, allowRemove = false, isUploading = false } = props;

  const [showAttachmentViewerModal, setShowAttachmentViewerModal] =
    useState(false);

  const [selectedAttachment, setSelectedAttachment] = useState<
    string | undefined
  >();

  const { data: chatMessageData } = useGetChatMessageQuery({
    variables: {
      input: {
        id: conversationId,
      },
    },
    fetchPolicy: 'cache-first',
  });

  const attachments = chatMessageData?.getChatMessage.attachments ?? [];
  const displayAttachments = attachments.length > 0 || isUploading;

  const [deleteAttachment] = useDeleteAttachmentMutation();
  const [getChatMessage] = useGetChatMessageLazyQuery();

  const onRemove = useCallback(
    async (id: string) => {
      const result = await deleteAttachment({
        variables: {
          input: {
            id,
          },
        },
      });

      if (result.data?.deleteAttachment) {
        await getChatMessage({
          variables: {
            input: {
              id: conversationId,
            },
          },
          fetchPolicy: 'network-only',
        });
      }
    },
    [conversationId, deleteAttachment, getChatMessage]
  );

  return (
    <>
      {displayAttachments && (
        <ImageList
          className="conversation-image-list"
          rowHeight={95}
          sx={{
            width: '100%',
            gridAutoFlow: 'column',
            gridTemplateColumns:
              'repeat(auto-fill,minmax(95px,1fr)) !important',
            gridAutoColumns: 'minmax(95px, 1fr)',
          }}
        >
          {isUploading && (
            <ImageListItem className="image-item-preview">
              <IonSpinner className="attachment-preview-spinner" />
            </ImageListItem>
          )}
          {attachments?.map((attachment) => {
            return (
              <ImagePreview
                key={attachment.id}
                attachment={attachment}
                resolution={ImageResolution.THUBMNAIL}
                onSelect={() => {
                  setSelectedAttachment(attachment.id);
                  setShowAttachmentViewerModal(true);
                }}
                onRemove={() => onRemove(attachment.id)}
                allowRemove={allowRemove}
              />
            );
          })}
        </ImageList>
      )}
      <AttachmentViewerModal
        initialAttachmentId={selectedAttachment}
        onDismiss={() => setShowAttachmentViewerModal(false)}
        showModal={showAttachmentViewerModal}
        source={AttachmentSourceType.CHAT_MESSAGE}
        sourceId={conversationId}
      />
    </>
  );
};

export { ConversationMessageAttachmentList };
